import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plane, Container, ChevronRight } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';

/**
 * Pantalla "Módulos" — punto de entrada a los diferentes módulos del ERP.
 *
 * Hoy expone los módulos "Aéreo" (/requests) y "LCL" (/lcl). La
 * estructura queda preparada para que más adelante se agreguen FCL,
 * Aduana, Terrestre, Triangulación, Tarifas, etc.
 *
 * Estilo consistente con el resto del ERP:
 *  - Componentes UI shadcn (Card, etc.)
 *  - Tipografía y espaciados del theme
 *  - Sombras, bordes redondeados, hover sutil
 *  - Animación con framer-motion (mismo approach que el resto de páginas)
 */
const ModulosPage = () => {
  const navigate = useNavigate();

  // Catálogo de módulos disponibles.
  // Cuando se agreguen más, se anexan al array y aparecen automáticamente
  // en el grid. La `to` apunta a la ruta existente del módulo.
  const modulos = [
    {
      id: 'aereo',
      titulo: 'Aéreo',
      descripcion: 'Gestión de operaciones aéreas',
      icon: Plane,
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-600',
      ringHover: 'hover:ring-sky-200',
      to: '/requests',
    },
    {
      id: 'lcl',
      titulo: 'LCL',
      descripcion: 'Gestión de carga marítima consolidada',
      icon: Container,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      ringHover: 'hover:ring-emerald-200',
      to: '/lcl',
    },
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Encabezado */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          Módulos
        </h1>
        <p className="text-muted-foreground mt-1">
          Selecciona un módulo para gestionar sus operaciones.
        </p>
      </motion.div>

      {/* Grid de módulos. 1 col en mobile, 2 en sm, 3 en lg.
          Por ahora solo hay 1 módulo, pero el grid ya queda preparado
          para los próximos sin necesidad de tocar estilos. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {modulos.map((m, idx) => {
          const Icon = m.icon;
          return (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: idx * 0.05, ease: 'easeOut' }}
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.98 }}
            >
              <Card
                role="button"
                tabIndex={0}
                onClick={() => navigate(m.to)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(m.to);
                  }
                }}
                className={[
                  'group cursor-pointer overflow-hidden',
                  'transition-all duration-300 ease-out',
                  'hover:shadow-xl hover:border-sky-300',
                  'focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-2',
                  m.ringHover,
                ].join(' ')}
              >
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
                  <div
                    className={[
                      'h-14 w-14 rounded-xl flex items-center justify-center',
                      'transition-transform duration-300 group-hover:scale-110',
                      m.iconBg,
                    ].join(' ')}
                  >
                    <Icon className={['h-7 w-7', m.iconColor].join(' ')} />
                  </div>
                  <ChevronRight
                    className={[
                      'h-5 w-5 text-muted-foreground/50',
                      'transition-all duration-300',
                      'group-hover:text-sky-600 group-hover:translate-x-1',
                    ].join(' ')}
                  />
                </CardHeader>
                <CardContent className="pt-0">
                  <CardTitle className="text-xl mb-1">{m.titulo}</CardTitle>
                  <CardDescription className="text-sm">
                    {m.descripcion}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default ModulosPage;
